export type RiskType = "danger" | "suspicious" | "trusted" | "unknown";

export type RiskDetails = {
  value: number;
  label: string;
};

const RISK_LABELS: Record<string, string> = {
  exchange: "Exchange",
  trusted_exchange: "Trusted Exchange",
  wallet: "Wallet",
  miner: "Miner",
  payment: "Payment",
  marketplace: "Marketplace",
  exchange_mlrisk_low: "Exchange (Low Risk)",
  p2p_exchange_mlrisk_low: "P2P Exchange (Low Risk)",
  seized_assets: "Seized Assets",
  p2p_exchange: "P2P Exchange",
  merchant_services: "Merchant Services",
  ico: "ICO",
  other: "Other",
  bridge: "Bridge",
  nft_platform_collection: "NFT Platform",

  atm: "ATM",
  risky_exchange: "Risky Exchange",
  exchange_mlrisk_moderate: "Exchange (Moderate Risk)",
  exchange_mlrisk_high: "Exchange (High Risk)",
  exchange_mlrisk_veryhigh: "Exchange (Very High Risk)",
  p2p_exchange_mlrisk_high: "P2P Exchange (High Risk)",
  liquidity_pools: "Liquidity Pools",
  infrastructure_as_a_service: "Infrastructure Service",
  decentralized_exchange_contract: "DEX Contract",
  smart_contract: "Smart Contract",
  token_smart_contract: "Token Contract",
  protocol_privacy: "Privacy Protocol",
  unnamed_service: "Unnamed Service",
  lending_contract: "Lending Contract",

  ransom: "Ransom",
  dark_market: "Dark Market",
  dark_service: "Dark Service",
  illegal_service: "Illegal Service",
  mixer: "Mixer",
  scam: "Scam",
  stolen_coins: "Stolen Coins",
  exchange_fraudulent: "Fraudulent Exchange",
  gambling: "Gambling",
  child_exploitation: "Child Exploitation",
  sanctions: "Sanctions",
  terrorism_financing: "Terrorism Financing",
  fraud_shop: "Fraud Shop",
  illicit_actor_org: "Illicit Actor",
  high_risk_jurisdiction: "High Risk Jurisdiction",
  enforcement_action: "Enforcement Action",
  special_measures: "Special Measures",
  online_pharmacy: "Online Pharmacy",
  malware: "Malware",

  unnamed_entity_low_risk: "Unnamed Entity Low Risk",
  unnamed_entity_medium_risk: "Unnamed Entity Medium Risk",
  unnamed_entity_high_risk: "Unnamed Entity High Risk",
} as const;

export const RiskKeys: Record<string, RISK_LABELS> = {
  EXCHANGE: "exchange",
  TRUSTED_EXCHANGE: "trusted_exchange",
  WALLET: "wallet",
  MINER: "miner",
  PAYMENT: "payment",
  MARKETPLACE: "marketplace",
  EXCHANGE_MLRISK_LOW: "exchange_mlrisk_low",
  P2P_EXCHANGE_MLRISK_LOW: "p2p_exchange_mlrisk_low",
  SEIZED_ASSETS: "seized_assets",
  P2P_EXCHANGE: "p2p_exchange",
  MERCHANT_SERVICES: "merchant_services",
  ICO: "ico",
  OTHER: "other",
  BRIDGE: "bridge",
  NFT_PLATFORM_COLLECTION: "nft_platform_collection",

  ATM: "atm",
  RISKY_EXCHANGE: "risky_exchange",
  EXCHANGE_MLRISK_MODERATE: "exchange_mlrisk_moderate",
  EXCHANGE_MLRISK_HIGH: "exchange_mlrisk_high",
  EXCHANGE_MLRISK_VERYHIGH: "exchange_mlrisk_veryhigh",
  P2P_EXCHANGE_MLRISK_HIGH: "p2p_exchange_mlrisk_high",
  LIQUIDITY_POOLS: "liquidity_pools",
  INFRASTRUCTURE_AS_A_SERVICE: "infrastructure_as_a_service",
  DECENTRALIZED_EXCHANGE_CONTRACT: "decentralized_exchange_contract",
  SMART_CONTRACT: "smart_contract",
  TOKEN_SMART_CONTRACT: "token_smart_contract",
  PROTOCOL_PRIVACY: "protocol_privacy",
  UNNAMED_SERVICE: "unnamed_service",
  LENDING_CONTRACT: "lending_contract",

  RANSOM: "ransom",
  DARK_MARKET: "dark_market",
  DARK_SERVICE: "dark_service",
  ILLEGAL_SERVICE: "illegal_service",
  MIXER: "mixer",
  SCAM: "scam",
  STOLEN_COINS: "stolen_coins",
  EXCHANGE_FRAUDULENT: "exchange_fraudulent",
  GAMBLING: "gambling",
  CHILD_EXPLOITATION: "child_exploitation",
  SANCTIONS: "sanctions",
  TERRORISM_FINANCING: "terrorism_financing",
  FRAUD_SHOP: "fraud_shop",
  ILLICIT_ACTOR_ORG: "illicit_actor_org",
  HIGH_RISK_JURISDICTION: "high_risk_jurisdiction",
  ENFORCEMENT_ACTION: "enforcement_action",
  SPECIAL_MEASURES: "special_measures",
  ONLINE_PHARMACY: "online_pharmacy",
  MALWARE: "malware",

  UNNAMED_ENTITY_LOW_RISK: "unnamed_entity_low_risk",
  UNNAMED_ENTITY_MEDIUM_RISK: "unnamed_entity_medium_risk",
  UNNAMED_ENTITY_HIGH_RISK: "unnamed_entity_high_risk",
} as const;

export const LOW_RISK_KEY_LIST = [
  RiskKeys.EXCHANGE,
  RiskKeys.TRUSTED_EXCHANGE,
  RiskKeys.WALLET,
  RiskKeys.MINER,
  RiskKeys.PAYMENT,
  RiskKeys.MARKETPLACE,
  RiskKeys.EXCHANGE_MLRISK_LOW,
  RiskKeys.P2P_EXCHANGE_MLRISK_LOW,
  RiskKeys.SEIZED_ASSETS,
  RiskKeys.P2P_EXCHANGE,
  RiskKeys.MERCHANT_SERVICES,
  RiskKeys.ICO,
  RiskKeys.OTHER,
  RiskKeys.BRIDGE,
  RiskKeys.NFT_PLATFORM_COLLECTION,
  RiskKeys.UNNAMED_ENTITY_LOW_RISK,
];

export const MIDDLE_RISK_KEY_LIST = [
  RiskKeys.ATM,
  RiskKeys.RISKY_EXCHANGE,
  RiskKeys.EXCHANGE_MLRISK_MODERATE,
  RiskKeys.EXCHANGE_MLRISK_HIGH,
  RiskKeys.EXCHANGE_MLRISK_VERYHIGH,
  RiskKeys.P2P_EXCHANGE_MLRISK_HIGH,
  RiskKeys.LIQUIDITY_POOLS,
  RiskKeys.INFRASTRUCTURE_AS_A_SERVICE,
  RiskKeys.DECENTRALIZED_EXCHANGE_CONTRACT,
  RiskKeys.SMART_CONTRACT,
  RiskKeys.TOKEN_SMART_CONTRACT,
  RiskKeys.PROTOCOL_PRIVACY,
  RiskKeys.UNNAMED_SERVICE,
  RiskKeys.LENDING_CONTRACT,
  RiskKeys.UNNAMED_ENTITY_MEDIUM_RISK,
];

export const HIGH_RISK_KEY_LIST = [
  RiskKeys.RANSOM,
  RiskKeys.DARK_MARKET,
  RiskKeys.DARK_SERVICE,
  RiskKeys.ILLEGAL_SERVICE,
  RiskKeys.MIXER,
  RiskKeys.SCAM,
  RiskKeys.STOLEN_COINS,
  RiskKeys.EXCHANGE_FRAUDULENT,
  RiskKeys.GAMBLING,
  RiskKeys.CHILD_EXPLOITATION,
  RiskKeys.SANCTIONS,
  RiskKeys.TERRORISM_FINANCING,
  RiskKeys.FRAUD_SHOP,
  RiskKeys.ILLICIT_ACTOR_ORG,
  RiskKeys.HIGH_RISK_JURISDICTION,
  RiskKeys.ENFORCEMENT_ACTION,
  RiskKeys.SPECIAL_MEASURES,
  RiskKeys.ONLINE_PHARMACY,
  RiskKeys.MALWARE,
  RiskKeys.UNNAMED_ENTITY_HIGH_RISK,
];

export const RISK_GROUPS: Record<RiskType, string[]> = {
  danger: HIGH_RISK_KEY_LIST,
  suspicious: MIDDLE_RISK_KEY_LIST,
  trusted: LOW_RISK_KEY_LIST,
  unknown: [],
};

export const CATEGORY_LABELS: Record<string, string> = {
  danger: "High Risk",
  suspicious: "Medium Risk",
  trusted: "Low Risk",
  sanctions: "Sanctions",
};

export { RISK_LABELS }

export type RISK_LABELS =
  (typeof RISK_LABELS)[keyof typeof RISK_LABELS];

